import { isDefined } from "@clipboard-health/util-ts";
import { Badge as MuiBadge, type BadgeProps as MuiBadgeProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";

export const BADGE_SIZES = ["small", "medium"] as const;
export type BadgeSize = typeof BADGE_SIZES[number];

interface BadgeProps
  extends Pick<
    MuiBadgeProps,
    "invisible" | "max" | "showZero" | "overlap" | "anchorOrigin" | "sx"
  > {
  count: number;
  children?: React.ReactNode;
  size?: BadgeSize;
  /**
   * The color of the border of the badge.
   * Defaults to border.darker.
   */
  borderColor?: (theme: Theme) => string;
  /**
   * The background color of the badge.
   * Defaults to intent.error.backgroundDark.
   */
  backgroundColor?: (theme: Theme) => string;
  /**
   * The color of the text of the badge.
   * Defaults to text.invert.
   */
  color?: (theme: Theme) => string;
}

/**
 * Simple component wrapping the MuiBadge component with custom styles.
 * Refer to https://mui.com/material-ui/react-badge/ for more details.
 */
export function Badge(props: BadgeProps) {
  const { count, size = "medium", borderColor, backgroundColor, color, sx, ...rest } = props;

  return (
    <MuiBadge
      {...rest}
      badgeContent={count}
      size={size}
      sx={mergeSxProps(
        (theme: Theme) => ({
          "& .MuiBadge-badge": {
            ...(isDefined(borderColor) && {
              borderColor: borderColor(theme),
            }),

            ...(isDefined(backgroundColor) && {
              backgroundColor: backgroundColor(theme),
            }),

            ...(isDefined(color) && {
              color: color(theme),
            }),
          },
        }),
        sx
      )}
    />
  );
}
